import React from 'react'
import PropTypes from 'prop-types'

import * as firebase from 'firebase'

import TimeAllocationTable from './TimeAllocationTable'
import JobType from './JobType'
import TimeAllocationExporter from '../common/TimeAllocationExporter'

export default class TimeAllocationTables extends React.Component {

  static propTypes = {
    filters: PropTypes.shape().isRequired,
    teamId: PropTypes.string.isRequired,
    teamMembers: PropTypes.array.isRequired
  }

  constructor(props) {
    super(props)
    this.calendarsListeners = {}
    this.timesheetsListeners = {}
    this.eventsListeners = {}
    this.jobs = {}
    this.timesheets = {}
    this.state = {
      jobs: 0,
      timesheets: 0
    }
  }

  componentWillReceiveProps(nextProps) {
    if (nextProps.filters) {
      const newState = {
        startDateStr: nextProps.filters.startDateStr,
        endDateStr: nextProps.filters.endDateStr,
        selectedWorkers: nextProps.filters.selectedWorkers,
        hideEmptyRows: nextProps.filters.settings.hideEmptyRows
      }
      if (this.state.startDateStr !== newState.startDateStr
        || this.state.endDateStr !== newState.endDateStr
        || this.state.selectedWorkers !== newState.selectedWorkers) {
        this.syncWorkersListeners(newState.selectedWorkers,
          newState.startDateStr, newState.endDateStr)
      }
      this.setState(newState)
    }
  }

  componentDidMount() {
    if (this.state.startDateStr && this.state.endDateStr) {
      this.syncWorkersListeners(this.state.selectedWorkers, this.state.startDateStr, this.state.endDateStr)
    }
  }

  componentWillUnmount() {
    this.detachCalendarsListeners()
    this.detachEventsListeners()
    this.detachTimesheetsListeners()
  }

  detachTimesheetListener = listener => {
    if (listener) {
      this.timesheetsListeners[listener.userId] = null
      firebase.database().ref(`timesheets/${listener.userId}@${this.props.teamId}`)
        .orderByKey().startAt(listener.startDateStr).endAt(listener.endDateStr)
        .off('value', listener.callback)
    }
  }

  detachCalendarListener = listener => {
    if (listener) {
      this.calendarsListeners[listener.userId] = null
      firebase.database().ref(`calendars/${listener.userId}@${this.props.teamId}/instances`)
        .orderByKey().startAt(listener.startDateStr).endAt(listener.endDateStr)
        .off('value', this.onUserCalendarValue)
    }
  }

  syncCalendarListener = (userId, startDateStr, endDateStr) => {
    this.calendarsListeners[userId] = { userId, startDateStr, endDateStr }
    firebase.database().ref(`calendars/${userId}@${this.props.teamId}/instances`)
      .orderByKey().startAt(startDateStr).endAt(endDateStr)
      .on('value', this.onUserCalendarValue)
  }

  syncTimesheetListener = (userId, startDateStr, endDateStr) => {
    // Store the reference to the attached function so we can release it on unmounts.
    const callback = firebase.database().ref(`timesheets/${userId}@${this.props.teamId}`)
      .orderByKey().startAt(startDateStr).endAt(endDateStr)
      .on('value', snapshot => this.onUserTimesheetValue(userId, snapshot))
    this.timesheetsListeners[userId] = { userId, startDateStr, endDateStr, callback }
  }

  syncWorkersListeners(selectedWorkers, startDateStr, endDateStr) {
    // Query calendars for each selected user
    // Will have a list of currently loaded calendars. On selected workers list updates, we don't need
    // to reset the whole list of listeners, just detach the unselected and attach the selected.
    // First check that the currently selected workers are all in sync
    const activeWorkers = selectedWorkers || []
    for (const worker of activeWorkers) {
      const listener = this.calendarsListeners[worker.userId]
      if (!listener || listener.startDateStr !== startDateStr || listener.endDateStr !== endDateStr) {
        this.detachCalendarListener(listener)
        this.detachTimesheetListener(listener)
        this.syncCalendarListener(worker.userId, startDateStr, endDateStr)
        this.syncTimesheetListener(worker.userId, startDateStr, endDateStr)
      }
    }
    // Then detach the ones that got unselected
    for (const workerId in this.calendarsListeners) {
      const worker = activeWorkers.find(w => w.userId === workerId)
      // If there is a listener to a worker that is not selected anymore, then detach that listener.
      if (!worker) {
        this.detachCalendarListener(this.calendarsListeners[workerId])
        this.detachTimesheetListener(this.timesheetsListeners[workerId])
      }
    }
  }

  detachTimesheetsListeners = () => {
    for (const workerId in this.timesheetsListeners) {
      this.detachTimesheetListener(this.timesheetsListeners[workerId])
    }
  }

  detachCalendarsListeners = () => {
    for (const workerId in this.calendarsListeners) {
      this.detachCalendarListener(this.calendarsListeners[workerId])
    }
  }

  detachEventListener = (eventId) => {
    firebase.database().ref(`events/${eventId}/commonInfo`)
      .off('value', this.onEventInfoValue)
    const listener = this.eventsListeners[eventId]
    if (listener && listener.lineItemsCallback) {
      firebase.database().ref(`events/${eventId}/extendedInfo/modules/lineItems`)
        .off('value', listener.lineItemsCallback)
    }
    if (listener && listener.clockingExtendedCallback) {
      firebase.database().ref(`events/${eventId}/extendedInfo/modules/clockingExtended`)
        .orderByKey().startAt(listener.startDateStr).endAt(listener.endDateStr)
        .off('value', listener.clockingExtendedCallback)
    }
    if (listener && listener.clockingWorkerCallback) {
      firebase.database().ref(`events/${eventId}/extendedInfo/modules/clockingWorker`)
        .orderByKey().startAt(listener.startDateStr).endAt(listener.endDateStr)
        .off('value', listener.clockingWorkerCallback)
    }
  }

  detachEventsListeners = () => {
    for (const eventId in this.eventsListeners) {
      this.detachEventListener(eventId)
    }
  }

  getJobTypeFromModules = modules => {
    if (!modules) {
      return null
    }
    if (modules.hasOwnProperty('lineItems')) {
      return JobType.Install
    }
    if (modules.hasOwnProperty('tasksRecurrent')) {
      return JobType.Maintenance
    }
    return JobType.Service
  }

  onEventInfoValue = snapshot => {
    const value = snapshot && snapshot.val()
    if (value) {
      if (!this.jobs[value.id]) {
        this.jobs[value.id] = {}
      }
      this.jobs[value.id] = Object.assign(this.jobs[value.id], {
        name: value.modules.displayName.value,
        type: this.getJobTypeFromModules(value.modules),
        laborersList: value.modules.laborers && value.modules.laborers.list || {},
        workersList: value.modules.workers && value.modules.workers.list || {}
      })
      // Will handle a flag to update, this will avoid copying the whole object
      // to trigger the state update
      this.setState({
        jobs: this.state.jobs + 1
      })
    }
  }

  onEventAllocationValue = (eventId, snapshot) => {
    const value = snapshot && snapshot.val()
    if (value) {
      if (!this.jobs[eventId]) {
        this.jobs[eventId] = {}
      }
      // Augment the job with it's line items info.
      this.jobs[eventId] = Object.assign(this.jobs[eventId], { lineItems: value })
      // Will handle a flag to update, this will avoid copying the whole object
      // to trigger the state update
      this.setState({
        jobs: this.state.jobs + 1
      })
    }
  }

  onEventSharedClockingValue = (eventId, snapshot) => {
    const value = snapshot && snapshot.val()
    if (value) {
      if (!this.jobs[eventId]) {
        this.jobs[eventId] = {}
      }
      // Augment the job with it's shared clockings info.
      this.jobs[eventId] = Object.assign(this.jobs[eventId], { sharedClockings: value })
      // Will handle a flag to update, this will avoid copying the whole object
      // to trigger the state update
      this.setState({
        jobs: this.state.jobs + 1
      })
    }
  }

  onEventWorkerClockingValue = (eventId, snapshot) => {
    const value = snapshot && snapshot.val()
    if (value) {
      if (!this.jobs[eventId]) {
        this.jobs[eventId] = {}
      }
      // Augment the job with it's workers clockings info.
      // Here clockings is computed by worker.
      this.jobs[eventId] = Object.assign(this.jobs[eventId], { workerClockings: value })
      // Will handle a flag to update, this will avoid copying the whole object
      // to trigger the state update
      this.setState({
        jobs: this.state.jobs + 1
      })
    }
  }

  syncEventListener = eventId => {
    // Start listening for the jobs (commonInfo),
    // the line items list and allocations (extendedInfo/lineItems) of this user
    // and the clockings (for service and recurrent/maintainance jobs)
    firebase.database().ref(`events/${eventId}/commonInfo`)
      .on('value', this.onEventInfoValue)
    // TODO: Optimize this query to listen for the line items list and the range of dates in the request
    const lineItemsCallback = firebase.database().ref(`events/${eventId}/extendedInfo/modules/lineItems`)
      .on('value', snapshot => this.onEventAllocationValue(eventId, snapshot))
    const clockingExtendedCallback = firebase.database().ref(`events/${eventId}/extendedInfo/modules/clockingExtended`)
      .orderByKey().startAt(this.state.startDateStr).endAt(this.state.endDateStr)
      .on('value', snapshot => this.onEventSharedClockingValue(eventId, snapshot))
    const clockingWorkerCallback = firebase.database().ref(`events/${eventId}/extendedInfo/modules/clockingWorker`)
      .orderByKey().startAt(this.state.startDateStr).endAt(this.state.endDateStr)
      .on('value', snapshot => this.onEventWorkerClockingValue(eventId, snapshot))
    this.eventsListeners[eventId] = {
      eventId,
      startDateStr: this.state.startDateStr,
      endDateStr: this.state.endDateStr,
      lineItemsCallback,
      clockingExtendedCallback,
      clockingWorkerCallback
    }
  }

  onUserCalendarValue = snapshot => {
    const instances = snapshot && snapshot.val()
    if (instances) {
      for (const dateStr in instances) {
        const instance = instances[dateStr]
        for (const eventId in instance) {
          this.detachEventListener(eventId)
          this.syncEventListener(eventId)
        }
      }
    }
  }

  onUserTimesheetValue = (workerId, snapshot) => {
    // Will handle a flag to update, this will avoid copying the whole object
    // to trigger the state update
    const value = snapshot && snapshot.val()
    if (value) {
      this.timesheets[workerId] = value
      this.setState({
        timesheets: this.state.timesheets + 1
      })
    }
  }

  exportCsv = () => {
    return TimeAllocationExporter.exportCsv(this.tables.map(t => t.getTableData()))
  }

  renderTables() {
    const views = []
    const selectedWorkersCount = this.state.selectedWorkers && this.state.selectedWorkers.length || 0
    if (selectedWorkersCount) {
      this.tables = new Array(selectedWorkersCount + 1)
      for (let i = 0; i < selectedWorkersCount; i++) {
        views.push(
          <TimeAllocationTable
            key={this.state.selectedWorkers[i].userId}
            ref={table => this.tables[i] = table}
            teamMembers={this.props.teamMembers}
            startDateStr={this.state.startDateStr}
            endDateStr={this.state.endDateStr}
            hideEmptyRows={this.state.hideEmptyRows}
            tableTarget='worker'
            workerId={this.state.selectedWorkers[i].userId}
            jobs={this.jobs}
            jobsVersion={this.state.jobs}
            timesheets={this.timesheets}
            timesheetsVersion={this.state.timesheets} />
        )
      }
      // Add the laborers totals at the end.
      views.push(
        <TimeAllocationTable
          key='laborers'
          ref={table => this.tables[selectedWorkersCount] = table}
          teamMembers={this.props.teamMembers}
          startDateStr={this.state.startDateStr}
          endDateStr={this.state.endDateStr}
          hideEmptyRows={this.state.hideEmptyRows}
          tableTarget='laborers'
          jobs={this.jobs}
          jobsVersion={this.state.jobs}
          timesheets={this.timesheets}
          timesheetsVersion={this.state.timesheets} />
      )
    }
    return views
  }

  render() {
    return (
      <div
        style={{ display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
        { this.renderTables() }
      </div>
    )
  }

}



// WEBPACK FOOTER //
// ./src/reports/timeAllocation/TimeAllocationTables.js